import { useState } from 'react';
import { Form } from 'formik';
import { FormField } from '@gokrew/ui/form/FormField';
import { Eye, EyeOff } from 'lucide-react';
import { KollabHRLogo } from '@gokrew/ui/assets';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import type { ResetPasswordForm } from '@gokrew/models/auth';
import { changePassword } from '@/api/auth';
import { resetTemporaryPasswordSchema } from '@gokrew/ui/validation/authSchema';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { FormikWithSchema } from '@gokrew/ui/form';
import { useDialog } from '@gokrew/ui/context';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import type { ApiResponse } from '@gokrew/models/generic';
import { FormikSubmitButton } from '@gokrew/ui/components/FormikSubmitButton';
import { PasswordValidationIndicator } from '@gokrew/ui/components/PasswordValidationIndicator';
import { useAuth } from '@/context/AuthContext';

interface InitialResetPasswordFormikValues extends ResetPasswordForm {
  confirmPassword: string;
}

interface InitialResetPasswordPageProps extends DialogComponentProps {
  dialogMode?: boolean;
}

export function InitialResetPasswordPage({
  dialogMode = false,
  dialogId,
}: InitialResetPasswordPageProps) {
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const { t } = useTranslation(TranslationNS_Admin.Auth);
  const { closeDialog } = useDialog();
  const { refreshUser } = useAuth();

  const initialValues: InitialResetPasswordFormikValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const resetMutation = useMutation<ApiResponse, Error, ResetPasswordForm>({
    mutationFn: (payload) => changePassword(payload),
    onSuccess: async () => {
      await refreshUser();
      dialogId && closeDialog(dialogId);
      toast.success(t('initial_reset_password.toast.success'));
    },
    onError: (error) => {
      toast.error(error.message || t('initial_reset_password.toast.error'));
    },
  });

  return (
    <div
      className={
        dialogMode
          ? 'p-1'
          : 'min-h-screen w-full flex items-center justify-center'
      }
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-4">
          <div className="flex justify-center w-full">
            <img className="w-13 mb-2" src={KollabHRLogo} alt="Logo" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900">
            {t('initial_reset_password.title')}
          </h2>

          <p className="text-gray-600 text-sm">
            {t('initial_reset_password.description')}
          </p>
        </div>

        <FormikWithSchema
          initialValues={initialValues}
          validationSchema={resetTemporaryPasswordSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload: ResetPasswordForm = {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
              };

              await resetMutation.mutateAsync(payload);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values }) => (
            <Form>
              <FormField
                name="currentPassword"
                type={showCurrentPass ? 'text' : 'password'}
                label={t('initial_reset_password.form.label.temporary_password')}
                placeholder={t(
                  'initial_reset_password.form.placeholder.temporary_password'
                )}
                IconSuffix={showCurrentPass ? Eye : EyeOff}
                onIconClick={() => setShowCurrentPass(!showCurrentPass)}
              />

              <FormField
                name="newPassword"
                type={showNewPass ? 'text' : 'password'}
                label={t('initial_reset_password.form.label.new_password')}
                placeholder={t(
                  'initial_reset_password.form.placeholder.new_password'
                )}
                IconSuffix={showNewPass ? Eye : EyeOff}
                onIconClick={() => setShowNewPass(!showNewPass)}
              />

              <FormField
                name="confirmPassword"
                type={showConfirmPass ? 'text' : 'password'}
                label={t('initial_reset_password.form.label.confirm_password')}
                placeholder={t(
                  'initial_reset_password.form.placeholder.confirm_password'
                )}
                IconSuffix={showConfirmPass ? Eye : EyeOff}
                onIconClick={() => setShowConfirmPass(!showConfirmPass)}
              />

              <PasswordValidationIndicator
                password={values.newPassword}
                confirmPassword={values.confirmPassword}
                showMatchValidation={true}
                className="my-4"
              />

              <FormikSubmitButton className="w-full mt-1.5">
                {t('initial_reset_password.button.submit')}
              </FormikSubmitButton>
            </Form>
          )}
        </FormikWithSchema>
      </div>
    </div>
  );
}
